/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  RawAxiosRequestHeaders,
} from "axios"
import { BaseQueryFn } from "@reduxjs/toolkit/query"
import {
  requestInterceptor,
  responseInterceptor,
} from "store/axios-interceptor"
import { generateAxiosHeader } from "utils/api_helper"
import { storageHelper } from "utils/storage"
import { apiBaseUrl } from "./constants"

/**
 * Single axios client instance for all API calls
 * Configured with interceptors matching Redux store for consistency
 * Uses full URLs (no baseURL) like Redux actions
 */
const createClient = (): AxiosInstance => {
  const axiosClient = axios.create({
    headers: generateAxiosHeader(),
    withCredentials: storageHelper.isCookieAuthEnabled(),
    responseType: "json",
  })

  // Apply the same interceptors used in Redux store
  const isDev = process.env.NODE_ENV === "development"
  requestInterceptor(axiosClient)
  responseInterceptor(axiosClient, isDev)

  return axiosClient
}

// Singleton client instance
export const apiClient = createClient()

/**
 * Helper to generate request headers with optional merchantID and merchantGroupID
 */
export const createRequestHeaders = (options?: {
  merchantID?: string
  merchantGroupID?: string
}): RawAxiosRequestHeaders => {
  const headers: RawAxiosRequestHeaders = {}

  if (options?.merchantID) {
    headers.merchantID = options.merchantID
  }
  if (options?.merchantGroupID) {
    headers.merchantGroupID = options.merchantGroupID
  }

  return headers
}

// ============================================================================
// RTK Query Custom Base Query (wraps existing axios client)
// ============================================================================

export interface AxiosBaseQueryArgs {
  url: string
  method?: AxiosRequestConfig["method"]
  data?: AxiosRequestConfig["data"]
  params?: AxiosRequestConfig["params"]
  headers?: RawAxiosRequestHeaders
}

export interface AxiosBaseQueryError {
  status?: number
  data?: unknown
  message?: string
}

/**
 * Custom base query for RTK Query that wraps the existing axios client.
 * This ensures all RTK Query requests use the same interceptors and config
 * as the rest of the app.
 *
 * Usage in createApi:
 * ```
 * const api = createApi({
 *   baseQuery: axiosBaseQuery('https://example.com'),
 *   endpoints: (builder) => ({
 *     getData: builder.query({
 *       query: (params) => ({
 *         url: '/api/data',  // Relative URL - baseUrl is prepended automatically
 *         params,
 *         headers: createRequestHeaders({ merchantID }),
 *       }),
 *     }),
 *   }),
 * })
 * ```
 */
export const axiosBaseQuery = (
  baseUrl: string = apiBaseUrl,
): BaseQueryFn<AxiosBaseQueryArgs, unknown, AxiosBaseQueryError> => async ({
  url,
  method = "GET",
  data,
  params,
  headers,
}) => {
  try {
    const result = await apiClient({
      url: baseUrl + url,
      method,
      data,
      params,
      headers,
    })
    return { data: result.data }
  } catch (axiosError) {
    const err = axiosError as AxiosError
    return {
      error: {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      },
    }
  }
}
